/**
 * Card — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/04-components.md § 2
 *
 * Three variants. Each enforces the documented radius, border, padding, and
 * elevation pattern (hairline borders + surface fill — never drop shadows
 * above y:2). Use the generic `Card` for one-off cards; use the named
 * variants below for the canonical placements.
 */

import type { ReactNode, HTMLAttributes } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * - `default`: bg paper-elevated, lg radius (20px), 1px line. Page-level cards.
   * - `nested`: bg white, md radius (12px), 1px line. Inside another card.
   * - `outlined`: bg transparent, dashed border. Empty/upload state.
   * - `interactive`: paper-elevated + active state press-feedback.
   */
  variant?: 'default' | 'nested' | 'outlined' | 'interactive';
  /** Add 24px padding (default true; pass false for custom inner layout). */
  padded?: boolean;
  children: ReactNode;
}

const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  default: 'bg-carnaf-paper-elevated border border-carnaf-line rounded-xl',
  nested: 'bg-white border border-carnaf-line rounded-md',
  outlined: 'bg-carnaf-paper border border-dashed border-carnaf-line rounded-md',
  interactive:
    'bg-carnaf-paper-elevated border border-carnaf-line rounded-xl active:bg-carnaf-paper-sunk transition-colors',
};

export function Card({
  variant = 'default',
  padded = true,
  children,
  className = '',
  ...rest
}: CardProps): JSX.Element {
  return (
    <div className={[variantClasses[variant], padded ? 'p-5' : '', className].join(' ')} {...rest}>
      {children}
    </div>
  );
}
